/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-products-list.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useBoolean, useTranslation } from 'hooks';
import { OrganisationProductFields, StudyFields } from 'queries/generated/fragments.generated';
import { ASSIGN_PRODUCTS, GET_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import Filter from 'views/components/core/filter';
import ItemList from 'views/components/core/item-list';

import 'views/components/admin/studies/admin-study.scss';

const AssignProductsListDialog = ({
    study,
    open,
    onClose,
    availableProducts,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    availableProducts: OrganisationProductFields[];
}) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState([]);
    const [filter, setFilter] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [assignProducts] = useMutation(ASSIGN_PRODUCTS);
    const saving = useBoolean(false);

    const close = (action?: any) => {
        setSelectedItems([]);
        onClose(action);
    };

    const getAvailableProducts = () =>
        availableProducts.filter((fun: any) => (!filter.length ? true : fun.name.toUpperCase().indexOf(filter) >= 0));

    useEffect(() => {
        if (open) {
            setFilteredProducts(getAvailableProducts());
        }
    }, [open, availableProducts, filter]);

    const errorRequest = () => {
        setSelectedItems([]);
        NotificationService.showNotification(t('errorMessage'));
        saving.setFalse();
    };

    const successRequest = (closeModal: boolean) => {
        saving.setFalse();
        NotificationService.showNotification(
            t('admin:productSucessfullyAssigned', { id: study.recordId, amount: selectedItems.length }),
        );
        setSelectedItems([]);
        if (closeModal) onClose();
    };

    const associate = (closeModal?: boolean) => {
        const studyProductIds = study.productsIds?.concat(selectedItems.map((selected) => selected.id));

        saving.setTrue();

        assignProducts({
            variables: { studyId: study.id, reason: '', studyProductIds },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest(closeModal);
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="assign-product-modal-title">
                {t('admin:assignProducts')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="assign-product-dialog-content assign-dialog-content pb-0">
                <>
                    <Filter id="assign-product-dialog-filter" onChange={({ text }: any) => setFilter(text)} />
                    <div className="list-info">
                        <p className="text-length">
                            {t('admin:productRecordsNum', { number: filteredProducts?.length })}
                        </p>
                    </div>
                    <ItemList
                        id="assign-product-list"
                        items={filteredProducts}
                        textField="name"
                        filter={filter}
                        multiselect
                        selectedItems={selectedItems}
                        onSelectionChange={(value: any) => {
                            saving.setFalse();
                            setSelectedItems(value);
                        }}
                    />
                </>
            </DialogContent>

            <DialogActions className="actions-assign-product flex-wrap pt-0">
                <div className="selected-items-container mb-20">
                    <p>
                        {selectedItems.length}/{filteredProducts?.length}
                    </p>
                </div>
                <Button
                    id="assign-product-new"
                    text="admin:addOrganizationProducts"
                    secondary
                    disabled={saving.value}
                    onClick={() => {
                        close('new-product');
                    }}
                />
                <Button
                    text="assign"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-product-save"
                    onClick={() => {
                        associate(false);
                    }}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-product-save-close"
                    onClick={() => {
                        associate(true);
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignProductsListDialog;
